/**
 * Transaction Range Chart
 * Visualizes deal size distribution over time
 * Shows funding and deal count by transaction range buckets
 */

import React, { useMemo, useState } from 'react';
import { withChartConfiguration, ChartProps, EnhancedChartProps } from './withChartConfiguration';
import { formatMonetaryValue, CHART_STYLES } from '../utils/chartUtils';
import {
  transactionRangeService,
  TransactionRangeResponse
} from '../services/TransactionRangeService';
import { Download, FileSpreadsheet, Pencil } from 'lucide-react';
import { useServiceData } from '../hooks/useChartData';
import { TitleEditor } from './TitleEditor';
import { customTitleService, TitleKey } from '../services/CustomTitleService';
import { ChartMetadata } from './ChartMetadata';
import { BRAND_COLORS, TYPOGRAPHY } from '../types/data';
import { getResponsiveFontSizes, getResponsiveChartHeight } from '../utils/responsiveChartUtils';
import '../styles/chart-components.css';

interface TransactionRangeChartProps extends ChartProps {
  groupBy: 'year' | 'quarter';
  timeframe: string;
  weekOffset: number;
  filters?: Record<string, string[] | undefined>;
  metadata?: {
    current_period: { start: string; end: string };
    cache_status?: string;
    response_time_ms?: number;
  };
}

type TransactionRangeChartWrapperProps = Omit<TransactionRangeChartProps, 'data'>;

/**
 * Configuration function for chart options
 */
const getTransactionRangeChartOption = (
  props: TransactionRangeChartProps & EnhancedChartProps
) => {
  const { data, groupBy, timeframe, filters } = props;

  if (!data?.data || !data?.ranges || !data?.periods) {
    return null;
  }

  const rangeData = data.data;
  const periods = data.periods;
  const ranges = data.ranges;

  // Check for custom title
  const titleKey: TitleKey = {
    chartType: 'transaction-range',
    timeframe,
    filters
  };
  const customTitle = customTitleService.getCustomTitle(titleKey);

  const title = customTitle?.title || 'Transaction Range Breakdown';
  const subtitle =
    customTitle?.subtitle ||
    `Deal size distribution by ${groupBy === 'quarter' ? 'quarter' : 'year'}`;

  // Sort ranges by predefined order (no filtering - show all ranges)
  const rangeOrder = transactionRangeService.getRangeOrder();
  const sortedRanges = ranges.sort((a, b) => {
    const aIndex = rangeOrder.indexOf(a);
    const bIndex = rangeOrder.indexOf(b);
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });

  // Calculate total deal count per period (all ranges)
  const totalDealCounts = periods.map(period => {
    let total = 0;
    sortedRanges.forEach(range => {
      const periodData = rangeData[range]?.[period];
      if (periodData?.deals) {
        total += periodData.deals;
      }
    });
    return total;
  });

  // Calculate total funding per period for label positioning
  const totalFundingPerPeriod = periods.map(period => {
    let total = 0;
    sortedRanges.forEach(range => {
      const periodData = rangeData[range]?.[period];
      if (periodData?.funding) {
        total += periodData.funding;
      }
    });
    return total;
  });

  // Prepare series data for stacked bar chart
  const seriesData: any[] = sortedRanges.map(range => ({
    name: range,
    type: 'bar',
    stack: 'total',
    emphasis: {
      focus: 'series'
    },
    itemStyle: {
      color: transactionRangeService.getRangeColor(range)
    },
    data: periods.map(period => {
      const periodData = rangeData[range]?.[period];
      return periodData ? periodData.funding : 0;
    })
  }));

  // Add invisible series to position labels at the top of stacks
  // NOTE: barGap: '-100%' overlays this series on the stack WITHOUT adding to Y-axis scale
  seriesData.push({
    name: 'Total Deals Label',
    type: 'bar',
    barGap: '-100%',  // Overlay on existing bars instead of stacking
    silent: true,
    itemStyle: {
      color: 'transparent',
      borderColor: 'transparent'
    },
    label: {
      show: true,
      position: 'top',
      formatter: (params: any) => {
        const dealCount = totalDealCounts[params.dataIndex];
        return `${dealCount} deals`;
      },
      fontSize: 14,
      fontWeight: 600,
      color: BRAND_COLORS.text,
      fontFamily: TYPOGRAPHY.fontFamily
    },
    data: totalFundingPerPeriod,
    tooltip: {
      show: false
    },
    legendHoverLink: false,
    emphasis: {
      disabled: true
    }
  });

  // Data description for Carta-style line
  const dataDescription = 'Total funding by transaction range bucket';

  // Get responsive font sizes for mobile/tablet/desktop
  const fontSizes = getResponsiveFontSizes();

  return {
    title: {
      text: title,
      subtext: subtitle,
      left: '1%',  // Match UnifiedChartFactory standards
      top: '1%',
      textStyle: {
        fontSize: fontSizes.title,  // Responsive: 18px mobile, 24px tablet, 32px desktop
        fontWeight: 700,
        color: BRAND_COLORS.text,
        fontFamily: TYPOGRAPHY.fontFamily
      },
      subtextStyle: {
        fontSize: fontSizes.subtitle,  // Responsive: 14px mobile, 16px tablet, 20px desktop
        fontWeight: 500,
        color: BRAND_COLORS.textSecondary,
        fontFamily: TYPOGRAPHY.fontFamily
      }
    },
    graphic: [
      // CTA separator line
      {
        type: 'line',
        left: '5%',
        right: '5%',
        bottom: '7%',
        shape: {
          x1: 0,
          y1: 0,
          x2: 1,
          y2: 0
        },
        style: {
          stroke: BRAND_COLORS.border,
          lineWidth: 1
        }
      },
      // CTA text
      {
        type: 'text',
        left: 'center',
        bottom: '3%',
        style: {
          text: '{normal|Subscribe for more cybersecurity market intelligence at }{purple|ReturnOnSecurity.com}',
          fontSize: 20,
          fontFamily: TYPOGRAPHY.fontFamily,
          textAlign: 'center',
          rich: {
            normal: {
              fontSize: 20,
              fontWeight: 400,
              fill: BRAND_COLORS.text
            },
            purple: {
              fontSize: 20,
              fontWeight: 700,
              fill: BRAND_COLORS.primary // #633FF3
            }
          }
        }
      }
    ],
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      backgroundColor: BRAND_COLORS.background,
      borderColor: BRAND_COLORS.border,
      borderWidth: 1,
      textStyle: {
        color: BRAND_COLORS.text,
        fontFamily: TYPOGRAPHY.fontFamily
      },
      formatter: (params: any) => {
        const period = params[0].axisValue;
        let html = `<div style="font-weight: 700; font-size: 13px; margin-bottom: 6px;">${period}</div>`;

        // Calculate total for this period
        let totalFunding = 0;
        let totalDeals = 0;

        params.forEach((param: any) => {
          const range = param.seriesName;
          const periodData = rangeData[range]?.[period];
          if (periodData) {
            totalFunding += periodData.funding;
            totalDeals += periodData.deals;
          }
        });

        // Keep legend order (params are already in series order)
        const sorted = [...params];

        sorted.forEach((param: any) => {
          if (param.value > 0) {
            const range = param.seriesName;
            const periodData = rangeData[range]?.[period];
            const funding = periodData?.funding || 0;
            const deals = periodData?.deals || 0;
            const pct = totalFunding > 0 ? (funding / totalFunding) * 100 : 0;

            html += `
              <div style="margin: 3px 0; font-size: 11px;">
                <div style="display: flex; align-items: center; gap: 8px;">
                  <span style="display: inline-block; width: 10px; height: 10px; background: ${param.color}; border-radius: 2px; flex-shrink: 0;"></span>
                  <span style="font-weight: 500; min-width: 65px;">${range}:</span>
                  <span style="font-weight: 700; color: ${BRAND_COLORS.primary};">${formatMonetaryValue(funding)}</span>
                  <span style="color: ${BRAND_COLORS.textSecondary};">(${pct.toFixed(1)}%)</span>
                </div>
                <div style="padding-left: 18px; color: ${BRAND_COLORS.textSecondary}; margin-top: 2px;">
                  <span style="font-weight: 600;">${deals}</span> ${deals === 1 ? 'deal' : 'deals'}
                </div>
              </div>
            `;
          }
        });

        html += `
          <div style="margin-top: 6px; padding-top: 4px; border-top: 1px solid ${BRAND_COLORS.border}; font-size: 11px;">
            <div style="display: flex; justify-content: space-between; font-weight: 700; margin-bottom: 3px;">
              <span>Total Funding:</span>
              <span>${formatMonetaryValue(totalFunding)}</span>
            </div>
            <div style="display: flex; justify-content: space-between; font-weight: 700;">
              <span>Total Deals:</span>
              <span style="color: ${BRAND_COLORS.primary};">${totalDeals}</span>
            </div>
          </div>
        `;

        return html;
      }
    },
    legend: {
      type: 'plain',  // Single-line legend
      orient: 'horizontal',
      bottom: CHART_STYLES.legend.bottom,
      left: CHART_STYLES.legend.alignment,
      data: sortedRanges,
      textStyle: {
        color: BRAND_COLORS.text,
        fontFamily: TYPOGRAPHY.fontFamily,
        fontSize: CHART_STYLES.legend.fontSize,
        fontWeight: CHART_STYLES.legend.fontWeight
      },
      itemGap: CHART_STYLES.legend.itemGap,
      itemWidth: 14,   // Slightly larger legend icons
      itemHeight: 14
    },
    grid: {
      left: CHART_STYLES.grid.left,
      right: CHART_STYLES.grid.right,
      top: CHART_STYLES.grid.top,
      bottom: CHART_STYLES.grid.bottom,
      containLabel: CHART_STYLES.grid.containLabel
    },
    xAxis: {
      type: 'category',
      data: periods,
      axisLabel: {
        fontSize: CHART_STYLES.xAxis.fontSize,
        fontWeight: CHART_STYLES.xAxis.fontWeight,
        color: BRAND_COLORS.text,
        fontFamily: TYPOGRAPHY.fontFamily,
        rotate: groupBy === 'quarter' ? 45 : 0
      },
      axisLine: {
        lineStyle: {
          color: BRAND_COLORS.border
        }
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        fontSize: CHART_STYLES.yAxis.fontSize,
        fontWeight: CHART_STYLES.yAxis.fontWeight,
        color: BRAND_COLORS.textSecondary,
        fontFamily: TYPOGRAPHY.fontFamily,
        formatter: (value: number) => {
          if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
          if (value >= 1e6) return `$${(value / 1e6).toFixed(0)}M`;
          if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
          return `$${value}`;
        }
      },
      splitLine: {
        lineStyle: {
          color: BRAND_COLORS.border,
          type: 'dashed'
        }
      }
    },
    series: seriesData
  };
};

/**
 * Base chart component that receives data and renders
 */
const TransactionRangeChartBase: React.FC<
  TransactionRangeChartProps & EnhancedChartProps
> = React.memo(({
  data,
  groupBy,
  timeframe,
  filters,
  chartRef,
  isLoading,
  error,
  downloadChart,
  refreshChart,
  metadata,
  ...restProps
}) => {
  const [editorOpen, setEditorOpen] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0);

  // Generate title key
  const titleKey: TitleKey = React.useMemo(
    () => ({
      chartType: 'transaction-range',
      timeframe,
      filters
    }),
    [timeframe, filters]
  );

  // Check for custom title
  const customTitle = React.useMemo(
    () => customTitleService.getCustomTitle(titleKey),
    [titleKey, forceRefresh]
  );

  // Refresh chart when forceRefresh changes
  React.useEffect(() => {
    if (forceRefresh > 0) {
      refreshChart();
    }
  }, [forceRefresh, refreshChart]);

  // Get chart option for title extraction
  const chartOption = React.useMemo(
    () =>
      getTransactionRangeChartOption({
        data,
        groupBy,
        timeframe,
        filters
      } as TransactionRangeChartProps & EnhancedChartProps),
    [data, groupBy, timeframe, filters, forceRefresh]
  );

  const titleOption = Array.isArray(chartOption?.title)
    ? chartOption.title[0]
    : chartOption?.title;
  const currentTitle = titleOption?.text || '';
  const currentSubtitle = titleOption?.subtext || '';

  const hasData = data?.data && data?.ranges && data?.periods && data.periods.length > 0;

  if (error) {
    return (
      <div className="chart-container-error">
        <div className="chart-error">
          <p className="chart-error-title">Error loading transaction range data</p>
          <p className="chart-error-message">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!hasData && !isLoading) {
    return (
      <div className="chart-container">
        <div className="chart-header">
          <h3 className="chart-title">Transaction Range Breakdown</h3>
        </div>
        <div className="chart-empty">
          <p className="chart-empty-message">
            No transaction range data available for this timeframe
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="chart-container-loading">
        <div className="chart-loading">
          <div className="chart-loading-spinner"></div>
        </div>
      </div>
    );
  }

  const handleCSVExport = () => {
    if (!data?.data || !data?.ranges || !data?.periods) return;

    const headers = ['Period', ...data.ranges];
    const rows = data.periods.map((period: string) => {
      const row = [period];
      data.ranges.forEach((range: string) => {
        const periodData = data.data[range]?.[period];
        row.push((periodData?.funding || 0).toString());
      });
      return row;
    });

    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `transaction-range-${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 relative">
        {/* Action buttons - hidden on mobile for less crowding */}
        <div className="hidden sm:flex absolute top-4 right-4 z-10 gap-1.5">
          <button
            onClick={() => setEditorOpen(true)}
            className="p-1.5 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 rounded shadow-sm border border-gray-300 dark:border-gray-600 transition-colors group"
            title="Edit chart title"
          >
            <Pencil className="w-3 h-3 text-gray-600 dark:text-gray-300 group-hover:text-purple-600 dark:group-hover:text-purple-400" />
            {customTitle && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-purple-600 rounded-full border border-white dark:border-gray-800" />
            )}
          </button>
          <button
            onClick={handleCSVExport}
            className="chart-button chart-button-secondary text-xs px-2 py-1.5"
            title="Export data as CSV"
            disabled={isLoading}
          >
            <FileSpreadsheet className="w-3 h-3" />
            CSV
          </button>
          <button
            onClick={() => downloadChart('blog')}
            className="chart-button chart-button-primary text-xs px-2 py-1.5"
            title="Download chart as PNG"
            disabled={isLoading}
          >
            <Download className="w-3 h-3" />
            PNG
          </button>
        </div>

        {/* Chart canvas */}
        <div
          ref={chartRef}
          className="w-full"
          style={{
            height: `${getResponsiveChartHeight(850, 'transaction')}px`,  // Responsive: 700px mobile, 680px tablet, 850px desktop
            minHeight: `${getResponsiveChartHeight(850, 'transaction')}px`
          }}
        />

        {/* Chart Metadata */}
        {metadata && (
          <ChartMetadata
            periodStart={metadata.current_period?.start}
            periodEnd={metadata.current_period?.end}
            cacheStatus={metadata.cache_status}
            responseTimeMs={metadata.response_time_ms}
          />
        )}
      </div>

      {/* Title Editor Modal */}
      <TitleEditor
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        currentTitle={currentTitle}
        currentSubtitle={currentSubtitle}
        onSave={(title, subtitle) => {
          customTitleService.setCustomTitle(titleKey, title, subtitle);
          setForceRefresh(prev => prev + 1);
          setEditorOpen(false);
        }}
        onReset={() => {
          customTitleService.resetCustomTitle(titleKey);
          setForceRefresh(prev => prev + 1);
          setEditorOpen(false);
        }}
        chartContext={{
          chartType: 'transaction-range',
          timeframe,
          groupBy
        }}
      />
    </>
  );
});

/**
 * Data fetching wrapper component
 */
const TransactionRangeChartWithData: React.FC<TransactionRangeChartWrapperProps> = ({
  groupBy,
  timeframe,
  weekOffset,
  filters,
  metadata
}) => {
  const { data, isLoading, error } = useServiceData(
    () =>
      transactionRangeService.getTransactionRangeBreakdown(
        groupBy,
        timeframe,
        weekOffset,
        filters
      ),
    [groupBy, timeframe, weekOffset, filters],
    {
      validationEnabled: false,
      retryOnError: true,
      maxRetries: 3
    }
  );

  const chartMetadata = data
    ? {
        current_period: data.metadata?.current_period || { start: '', end: '' },
        cache_status: data.cache_status,
        response_time_ms: data.response_time_ms
      }
    : undefined;

  return (
    <TransactionRangeChartWithHOC
      data={data}
      groupBy={groupBy}
      timeframe={timeframe}
      weekOffset={weekOffset}
      filters={filters}
      metadata={chartMetadata}
      isLoading={isLoading}
      error={error}
    />
  );
};

// Create the HOC-wrapped component
const TransactionRangeChartWithHOC = withChartConfiguration(
  TransactionRangeChartBase,
  getTransactionRangeChartOption
);

export default TransactionRangeChartWithData;
